import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useMemberOrchestrator } from "@nju/data-access-portal-member";
import { useOfferMode } from "@nju/data-access-portal-offers";
import { pushSimpleEvent } from "@nju/data-layer";
import {
  ErrorMessage,
  FullSimCardNumberField,
  REPLACE_SPACES,
} from "@nju/form";
import { portalRoutes } from "@nju/routes";
import { A, Button, Panel, Separator, Text } from "@nju/ui";
import { GeneralError } from "../partials";
import { ISimCardFlow } from "../types";
import { mobileFullSimCardMachine } from "./mobileFullSimCard.machine";

interface IMobileFullSimCardPageProps {
  flow: ISimCardFlow;
}

export function MobileFullSimCardPage({ flow }: IMobileFullSimCardPageProps) {
  const router = useRouter();
  const mode = useOfferMode();
  const memberOrchestrator = useMemberOrchestrator();
  const selectedOrchestrator = memberOrchestrator?.mobile;

  if (!selectedOrchestrator) {
    throw new Error("Missing orchestrator");
  }

  const { id, userId } = selectedOrchestrator;

  const [current, send] = useMachine(mobileFullSimCardMachine, {
    actions: {
      setIccIdSuccess: (_, event) => {
        if (event.type === "ICC_ID_SET_SUCCESS") {
          router.push(
            portalRoutes.mojeKonto.index.get({
              mode,
              ...(userId && { userId }),
            }),
          );
        }
      },
    },
    context: {
      flow,
      orchestratorId: id,
    },
  });

  const { context, matches } = current;
  const { iccId, iccIdError } = context;

  React.useEffect(() => {
    if (iccIdError) {
      pushSimpleEvent({
        event: "error_add_sim_card",
        eventName: "WrongNumberFull",
      });
    }
  }, [iccIdError]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();
        send({ type: "SUBMIT" });
      },
      [send],
    );

  const onInputChange = React.useCallback(
    (value: string) => {
      send({
        type: "SET_ICC_ID",
        value: value.replace(REPLACE_SPACES, ""),
      });
    },
    [send],
  );

  if (matches("generalError")) {
    return <GeneralError />;
  }

  return (
    <Panel>
      <Text
        size={{
          "@onlyMobile": "xl",
          "@fromTablet": "keyS",
        }}
        weight="bold"
      >
        Wpisz numer karty SIM
      </Text>
      <Separator size="xs" tone="invisible" />
      <Text
        size={{
          "@onlyMobile": "m",
          "@fromTablet": "l",
        }}
      >
        wpisz 19 cyfr numeru SIM.
      </Text>
      <Separator size="s" tone="invisible" />
      <form onSubmit={handleSubmit}>
        <div className="from-tablet:max-w-[400px]">
          <FullSimCardNumberField
            defaultValue={iccId}
            label="numer karty SIM"
            onValueChange={onInputChange}
            state={iccIdError ? "error" : undefined}
          />
        </div>
        {iccIdError && (
          <>
            <Separator size="xxs" tone="invisible" />
            <ErrorMessage message={iccIdError} />
          </>
        )}
        <Separator size="xs" tone="invisible" />
        <div className="flex justify-between only-mobile:gap-3 only-mobile:flex-col-reverse flex-row">
          <Link
            href={portalRoutes.mojeKonto.index.get({
              mode,
              ...(userId && { userId }),
            })}
            passHref={true}
          >
            <A
              size={{
                "@onlyMobile": "s",
              }}
              variant="secondary"
            >
              anuluj
            </A>
          </Link>
          <Button
            isLoading={!matches("ready")}
            size={{ "@onlyMobile": "s" }}
            type="submit"
          >
            dalej
          </Button>
        </div>
      </form>
    </Panel>
  );
}
